'use client';
import {
    Box,
    Container,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import { FaqLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { useEffect } from "react";
import faqService from "@/app/_services/faqService";

interface FaqItem {
    icon: React.ReactNode;
    question: string;
    answer: string | string[];
}

function FaqAccordion({ item }: { item: FaqItem }) {
    return (
        <Accordion
            disableGutters
            sx={{
                fontFamily: "inherit",
                background: "rgba(255, 255, 255, 0.12)",
                color: "#e5e7eb",
                borderRadius: "12px !important",
                border: "none",
                boxShadow: "none",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff", flexShrink: 0 }} />}
                sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    minHeight: 56,
                    "& .MuiAccordionSummary-content": {
                        margin: 0,
                        flexGrow: 1,
                        alignItems: "center",
                        gap: 1.5,
                        justifyContent: "flex-start",
                        textAlign: "left",
                    },
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        color: "#fff",
                    }}
                >
                    {item.icon}
                </Box>
                <Typography
                    component="h2"
                    sx={{
                        fontFamily: "inherit",
                        fontWeight: 500,
                        fontSize: { xs: "0.9375rem", sm: "1rem" },
                        color: "#fff",
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: { xs: "normal", md: "nowrap" },
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                    }}
                >
                    {item.question}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, textAlign: "left" }}>
                {!Array.isArray(item.answer) ? (
                    <Typography
                        sx={{
                            fontFamily: "inherit",
                            color: "rgba(203, 213, 245, 0.95)",
                            lineHeight: 1.7,
                            fontSize: "0.9375rem",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                        }}
                    >
                        {item.answer}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            sx={{
                                fontFamily: "inherit",
                                color: "rgba(203, 213, 245, 0.95)",
                                mb: 1,
                                fontSize: "0.9375rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                            }}
                        >
                            {item.answer[0]}
                        </Typography>
                        <Box
                            component="ul"
                            sx={{
                                pl: { xs: 2, sm: 3 },
                                pr: 0,
                                m: 0,
                                color: "rgba(203, 213, 245, 0.95)",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                            }}
                        >
                            {item.answer.slice(1).map((ans, idx) => (
                                <Typography
                                    component="li"
                                    key={idx}
                                    sx={{
                                        fontFamily: "inherit",
                                        color: "rgba(203, 213, 245, 0.95)",
                                        lineHeight: 1.7,
                                        fontSize: "0.9375rem",
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                    }}
                                >
                                    {ans}
                                </Typography>
                            ))}
                        </Box>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

const faqData = [
    {
         icon: <DescriptionOutlinedIcon />,
        question: "How does the waitlist work?",
        answer:
            "First, fill out the waitlist form. We go through it chronologically and when we reach your spot, we will send you the info about any upcoming shows.",
    },
    {
        icon: <StarOutlineOutlinedIcon />,
        question: "If I've already been to a show, do I need to re-signup for the waitlist?",
        answer:
            "Yes, but always feel free to DM us if you're unsure.",
    },
    {
        icon: <LocalOfferOutlinedIcon />,
        question: "What if i want a refund?",
        answer:
            "You can get a refund up to 72 hours before showtime.",
    },
    {
       icon: <SyncAltOutlinedIcon />,
        question: "Can I transfer my ticket?",
        answer:
            "Yes, but tell your friend to use your name at the door.",
    },
    {
       icon: <ReplayOutlinedIcon />,
        question: "Do you ever repeat venues?",
        answer:
            "Yes, we love going back to our favorite venues. Some, like Astor Place Hairstylists, we do multiple times a year. Others, like Katz's Deli and Economy Candy, are annual events!",
    },
    {
        icon: <BusinessOutlinedIcon />,
        question: "What is the name of our business?",
        answer: [
            "It depends who's asking.",
            "Underground Overground Comedy",
            "Underground Overground, LLC",
            "Comedy UO",
            "Comedy overground underground",
            "Comedyuo",
            "Comedy.uo",
            "UOcomedy"],
    },
    {
        icon: <InfoOutlinedIcon />,
        question: "How are you different than a regular comedy club?",
        answer:
            "ComedyUO is a pop-up comedy show, appearing in strange places like laundromats, barbershops, restaurants, and more. Our tickets are all inclusive, so if you're looking for a top tier comedy show near you, look us up and grab a ticket. We bring you an end-to-end experience without the stress of racking up the bill.",
    },
    {
        icon: <PersonOutlineOutlinedIcon />,
        question: "Who do you feature in your lineups?",
        answer:
            "We bring in top-tier talent you'd recognize from Netflix, SNL, and late-night TV. Our lineups stay under wraps because our audience trusts us, and half the fun is the surprise!",
    },
    {
        icon: <WorkOutlineIcon />,
        question: "Do you offer private or corporate comedy events?",
        answer:
            "Yes. We produce private comedy and magic events for companies, brands, and organizations across NYC. We are able to produce events in any of the 85 venues in our rolodex, allowing us to produce unforgettable private events for teams, clients, and partners. From law firms and financial institutions to startups and tech companies, we deliver bespoke, turnkey live entertainment with the same level of talent, production, and care as our public shows. Our shows are completely customizable, from F&B, to talent, to the venue, we will cater to your needs.",
    },
    {
        icon: <CampaignIcon />,
        question: "Do you host branded or sponsored comedy shows?",
        answer:
            "We work with brands to create custom comedy experiences, sponsor our tentpole shows, and produce branded content through comedy tapings. Sponsorship opportunities include on-site branding, social media distribution, email promotion, and content that lives beyond the event.",
    },
    {
        icon: <FavoriteBorderOutlinedIcon sx={{ fontSize: 18 }} />,
        question: "Are we a good date night spot?",
        answer:
            "If you're on a 1st date, enter at your own risk. Couples and double dates come to shows all the time, but if you're looking to bring that crush from the office you're trying to impress for the first time, be ready for some crowdwork!",
    },
];

export default function FAQSection() {

useEffect(() => {
getFAQData()
}, []);

const getFAQData = async () => {
  const data = await faqService.getFAQ();
  console.log(data);
}

    return (
     <>
     <FaqLayoutHelmet />
     <Header />
        <Box
            component="main"
            sx={{
                position: "relative",
                backgroundImage: "url('/images/Ethan_And_David_-_Willy_Wonka_2.webp')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                minHeight: { xs: "min-content", md: "90vh" },
                display: "flex",
                alignItems: "center",
                fontFamily: '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                boxSizing: "border-box",
                py: { xs: 3, md: 0 },
            }}
        >
            <Container
                maxWidth="xl"
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    zIndex: 2,
                    px: { xs: 2, sm: 3, md: 3 },
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                }}
            >
                {/* CONTENT PANEL - darker, left-aligned */}
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 1400,
                        mx: "auto",
                        px: { xs: 1.5, sm: 2, md: 4 },
                        py: { xs: 3, sm: 4, md: 5 },
                        background: "rgba(3, 7, 18, 0.92)",
                        borderRadius: 2,
                        textAlign: "left",
                        boxSizing: "border-box",
                        overflowX: "hidden",
                    }}
                >
                    {/* Section label - small uppercase */}
                    <Typography
                        component="span"
                        sx={{
                            display: "block",
                            fontFamily: "inherit",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            letterSpacing: { xs: "0.08em", sm: "0.12em" },
                            color: "#e1e5f1",
                            textTransform: "uppercase",
                            mb: 1,
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                        }}
                    >
                        FAQ
                    </Typography>
                    {/* Main heading - one line */}
                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: "inherit",
                            fontWeight: 700,
                            fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
                            lineHeight: 1.15,
                            letterSpacing: "-0.02em",
                            color: "#fff",
                            mb: 2,
                            wordBreak: "break-word",
                        }}
                    >
                        Frequently Asked Questions
                    </Typography>
                    {/* Subtitle */}
                    <Typography
                        sx={{
                            fontFamily: "inherit",
                            fontSize: { xs: "0.9375rem", sm: "1rem" },
                            fontWeight: 400,
                            color: "rgba(225, 229, 241, 0.85)",
                            mb: 3,
                            maxWidth: 520,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                        }}
                    >
                        Everything you need to know about our shows, tickets, and more.
                    </Typography>
                    {/* FAQ LIST - two columns, darker cards, left-aligned questions */}
                    <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {faqData.slice(0, Math.ceil(faqData.length / 2)).map((item, index) => (
                                    <FaqAccordion key={index} item={item} />
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {faqData.slice(Math.ceil(faqData.length / 2)).map((item, index) => (
                                    <FaqAccordion key={index} item={item} />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
        <Footer /></>

    );
}
